import { CONFIG } from './config';

const AUTH_KEY = 'ep:auth';
const VERIFIER_KEY = 'ep:pkce.verifier';

interface Tokens {
  accessToken: string;
  idToken: string;
  expiresAt: number;
  refreshToken?: string;
}

function getTokens(): Tokens | null {
  try { return JSON.parse(localStorage.getItem(AUTH_KEY) ?? 'null'); }
  catch { return null; }
}

function setTokens(d: Tokens): void {
  localStorage.setItem(AUTH_KEY, JSON.stringify(d));
}

export function clearTokens(): void {
  localStorage.removeItem(AUTH_KEY);
  localStorage.removeItem(VERIFIER_KEY);
}

async function generatePKCE(): Promise<{ verifier: string; challenge: string }> {
  const arr = new Uint8Array(32);
  crypto.getRandomValues(arr);
  const verifier = btoa(String.fromCharCode(...arr))
    .replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
  const hash = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
  const challenge = btoa(String.fromCharCode(...new Uint8Array(hash)))
    .replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
  return { verifier, challenge };
}

function getRedirectUri(): string {
  // Prefer the explicitly configured URI so Cognito always receives exactly
  // what's registered — avoids trailing-slash and iOS PWA pathname edge cases.
  if (CONFIG.redirectUri) return CONFIG.redirectUri;
  return window.location.href.split('?')[0].split('#')[0];
}

export async function login(cognitoDomain: string, clientId: string): Promise<void> {
  const { verifier, challenge } = await generatePKCE();
  localStorage.setItem(VERIFIER_KEY, verifier);
  const params = new URLSearchParams({
    response_type: 'code',
    client_id: clientId,
    redirect_uri: getRedirectUri(),
    scope: 'openid email profile',
    code_challenge_method: 'S256',
    code_challenge: challenge,
  });
  window.location.href = `https://${cognitoDomain}/oauth2/authorize?${params}`;
}

// Resolves true once the page is reloading (the caller should stop), false when there was no code to exchange.
export async function handleCallback(cognitoDomain: string, clientId: string): Promise<boolean> {
  const code = new URLSearchParams(window.location.search).get('code');
  if (!code) return false;
  const verifier = localStorage.getItem(VERIFIER_KEY);
  if (!verifier) throw new Error('Missing PKCE verifier');
  const resp = await fetch(`https://${cognitoDomain}/oauth2/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({
      grant_type: 'authorization_code',
      client_id: clientId,
      code,
      redirect_uri: getRedirectUri(),
      code_verifier: verifier,
    }),
  });
  if (!resp.ok) throw new Error(`Token exchange failed: ${resp.status}`);
  const data = await resp.json() as {
    access_token: string; id_token: string; expires_in: number; refresh_token?: string;
  };
  setTokens({
    accessToken: data.access_token,
    idToken: data.id_token,
    expiresAt: Date.now() + data.expires_in * 1000,
    refreshToken: data.refresh_token,
  });
  localStorage.removeItem(VERIFIER_KEY);
  // Reload to the base path so PlanningPermissionsProvider mounts with tokens already in localStorage.
  // replaceState() silently updates the URL but keeps the existing React tree mounted with userId=null.
  window.location.replace(`${window.location.pathname}#/assess`);
  return true;
}

export function getAccessToken(): string | null {
  const tokens = getTokens();
  if (!tokens) return null;
  if (Date.now() >= tokens.expiresAt - 30_000) return null;
  return tokens.accessToken;
}

let renewing: Promise<string | null> | null = null;

/**
 * A usable access token, renewing it first if it has lapsed. A phone freezes timers in the background (and a browser tab
 * slows them), so the scheduled refresh can be late: a request made on coming back renews the token itself rather than
 * failing. Requests that find it lapsed at the same moment share one renewal. Null when the sign-in itself has expired.
 */
export async function ensureAccessToken(): Promise<string | null> {
  const current = getAccessToken();
  if (current) return current;
  if (!getTokens()?.refreshToken) return null;
  renewing ??= refreshAccessToken(CONFIG.cognitoDomain, CONFIG.clientId)
    .then(() => getAccessToken())
    .finally(() => { renewing = null; });
  return renewing;
}

export function getTokenExpiresAt(): number | null {
  return getTokens()?.expiresAt ?? null;
}

export async function refreshAccessToken(
  cognitoDomain: string,
  clientId: string,
): Promise<'ok' | 'expired' | 'error'> {
  const tokens = getTokens();
  if (!tokens?.refreshToken) return 'expired';
  try {
    const resp = await fetch(`https://${cognitoDomain}/oauth2/token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams({
        grant_type: 'refresh_token',
        client_id: clientId,
        refresh_token: tokens.refreshToken,
      }),
    });
    if (resp.status === 400) return 'expired';
    if (!resp.ok) return 'error';
    const data = await resp.json() as { access_token: string; id_token: string; expires_in: number };
    // Cognito does not rotate the refresh token on use — preserve the original
    setTokens({
      accessToken: data.access_token,
      idToken: data.id_token,
      expiresAt: Date.now() + data.expires_in * 1000,
      refreshToken: tokens.refreshToken,
    });
    return 'ok';
  } catch {
    return 'error';
  }
}

export function getUserEmail(): string | null {
  const tokens = getTokens();
  if (!tokens?.idToken) return null;
  try {
    const payload = JSON.parse(atob(
      tokens.idToken.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')
    )) as { email?: string };
    return payload.email ?? null;
  } catch { return null; }
}

export function getUserSub(): string | null {
  const tokens = getTokens();
  if (!tokens?.idToken) return null;
  try {
    const payload = JSON.parse(atob(
      tokens.idToken.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')
    )) as { sub?: string };
    return payload.sub ?? null;
  } catch { return null; }
}

export function getUserGroups(): string[] {
  const tokens = getTokens();
  if (!tokens?.idToken) return [];
  try {
    const payload = JSON.parse(atob(
      tokens.idToken.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')
    )) as { 'cognito:groups'?: string[] };
    return payload['cognito:groups'] ?? [];
  } catch { return []; }
}

export function logout(cognitoDomain: string, clientId: string): void {
  clearTokens();
  window.location.href =
    `https://${cognitoDomain}/logout?client_id=${encodeURIComponent(clientId)}&logout_uri=${encodeURIComponent(getRedirectUri())}`;
}
